// ---// EmployeeMapping.tsx
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../../core/common/collapse-header/collapse-header";
import Table from "../../../../core/common/dataTable/index";
import { AppDispatch, RootState } from "../../../../core/data/redux/store";
import { useDispatch, useSelector } from "react-redux";
import { deleteEsicEmployee, fetchEsicEmployees } from "../../../../core/data/redux/payroll/ESIC/employeeMapping/employeemappingAction";
import { getEmployees } from "../../../../core/data/redux/employee/employeeAction";
import DeleteModal1 from "../../../../core/modals/deleteModal1";
import { exportToCSV, exportToExcel, exportToPDF } from "../../../../core/common/GlobalFunctions/exportTableData";
import { Space, message } from "antd";
import { decryptData } from "../../../../core/utils/encryptUtils";
import dayjs from "dayjs";
import EmployeeMappingDrawer from '../modals/employeeMappingModal';
import { formatDate } from "../../../../core/utils/configFormatters";
import { useAppConfig } from "../../../../customhooks/useAppConfig";

const EmployeeMapping: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editRecord, setEditRecord] = useState<any | null>(null);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [selectedStatus, setSelectedStatus] = useState("active");
    const [selectedSort, setSelectedSort] = useState("Recently Added");
    const [tableKey, setTableKey] = useState(0);

    const { records: esicEmployees = [] } = useSelector((state: RootState) => state.esic.esicEmployee);
    const { employees: allEmployees = [], loading: allEmployeesLoading } = useSelector((state: RootState) => state.employee);



    const encryptedPermissions = localStorage.getItem("currentPermissions");
    const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || "")) ? decryptData(encryptedPermissions || "") : [];


    const canAdd = permissions.includes("Add") || permissions.includes("AllowAll");
    const canView = permissions.includes("View") || permissions.includes("AllowAll");
    const canEdit = permissions.includes("Edit") || permissions.includes("AllowAll");
    const canDelete = permissions.includes("Delete") || permissions.includes("AllowAll");
    const canExport = permissions.includes("Export") || permissions.includes("AllowAll");


    useEffect(() => {
        dispatch(fetchEsicEmployees());
        dispatch(getEmployees());
    }, [dispatch]);



    const handleOpenDrawer = (record: any | null) => {
        setEditRecord(record);
        setDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setDrawerOpen(false);
        setEditRecord(null);
    };

    const handleDelete = async () => {
        if (!editRecord?.esic_emp_id) return;
        try {
            await dispatch(deleteEsicEmployee(editRecord.esic_emp_id)).unwrap();
            message.success("Employee deleted successfully");
            setIsDeleteModalVisible(false);
            await dispatch(fetchEsicEmployees());
        } catch (error: any) {
            message.error(error?.message || "Failed to delete");
        }
    };


    const filteredData = useMemo(() => {
        let sortedData = [...esicEmployees];
        if (selectedStatus && selectedStatus !== "all") {
            sortedData = sortedData.filter((emp) => (emp.status ?? '').toLowerCase() === selectedStatus);
        }
        if (selectedSort === "Ascending") {
            sortedData.sort((a, b) => (a.employee_id || 0) - (b.employee_id || 0));
        } else if (selectedSort === "Descending") {
            sortedData.sort((a, b) => (b.employee_id || 0) - (a.employee_id || 0));
        } else if (selectedSort === "Recently Added") {
            sortedData.sort((a, b) => (b.esic_emp_id || 0) - (a.esic_emp_id || 0));
        }
        return canView ? sortedData : [];
    }, [esicEmployees, selectedStatus, selectedSort, canView]);

    useEffect(() => {
        setTableKey((prevKey) => prevKey + 1);
    }, [dispatch, esicEmployees, selectedStatus, selectedSort]);

    const config = useAppConfig();
    if (!config) return null;

    const handleExport = (format: "excel" | "csv" | "pdf") => {
    const exportRows = esicEmployees.map((emp: any, idx: number) => ({
        "S.No": idx + 1,
        "ESIC Number": emp.esic_number,
        "ESIC Branch": emp.branch_name,
        "Enrollment Date": formatDate(emp.esic_enrollment_date, config),
        "Applicable": emp.is_esic_applicable === 1 ? 'Yes' : 'No',
        "Remarks": emp.remarks,
    }));

    if (format === "excel") {
        exportToExcel(exportRows, "esic_employees");
    } else if (format === "csv") {
        exportToCSV(exportRows, "esic_employees");
    } else if (format === "pdf"){
        exportToPDF(exportRows, "esic_employees");
    }
};


    const columns = [
        {
            title: "Employee Name",
            dataIndex: "Name",
            sorter: (a: any, b: any) => (a.employee_id || 0) - (b.employee_id || 0),
        },
        {
            title: "ESIC Number",
            dataIndex: "esic_number",
            sorter: (a: any, b: any) => (a.esic_number || '').localeCompare(b.esic_number || ''),
        },
        {
            title: "ESIC Branch",
            dataIndex: "branch_name",
            sorter: (a: any, b: any) => (a.branch_name || '').localeCompare(b.branch_name || ''),
        },
        {
            title: "Enrollment Date",
            dataIndex: "esic_enrollment_date",
            render: (text: string) => dayjs(text).format('YYYY-MM-DD'),
            sorter: (a: any, b: any) => dayjs(a.esic_enrollment_date).valueOf() - dayjs(b.esic_enrollment_date).valueOf(),
        },
        {
            title: "Applicable",
            dataIndex: "is_esic_applicable",
            render: (text: number) => <span>{text === 1 ? 'Yes' : 'No'}</span>,
            sorter: (a: any, b: any) => (a.is_esic_applicable || 0) - (b.is_esic_applicable || 0),
        },
        {
            title: "Action",
            dataIndex: "actions",
            render: (_: any, record: any) => (
                <Space>
                    <button
                        className="action-icon me-2"
                        disabled={!canEdit}
                        onClick={() => handleOpenDrawer(record)}
                        title={!canEdit ? "Can't edit" : "Edit"}
                        style={{ cursor: canEdit ? "pointer" : "not-allowed", background: "none", border: "none" }}
                    >
                        <i className="ti ti-edit" />
                    </button>

                    <button
                        className="action-icon"
                        onClick={() => { setEditRecord(record); setIsDeleteModalVisible(true); }}
                        disabled={!canDelete}
                        title={!canDelete ? "Can't delete" : "Delete"}
                        style={{ cursor: canDelete ? "pointer" : "not-allowed", background: "none", border: "none" }}
                    >
                        <i className="ti ti-trash" />
                    </button>
                </Space>
            ),
        },
    ];
    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                    <div className="my-auto mb-2">
                        <h2 className="mb-1">ESIC Employee Mapping</h2>
                        <nav>
                            <ol className="breadcrumb mb-0">
                                <li className="breadcrumb-item">
                                    <Link to="#"><i className="ti ti-smart-home" /></Link>
                                </li>
                                <li className="breadcrumb-item active" aria-current="page">
                                    ESIC Employee Mapping
                                </li>
                            </ol>
                        </nav>
                    </div>

                    <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                        {canExport && (
                            <div className="me-2 mb-2">
                                <div className="dropdown">
                                    <Link to="#" className="dropdown-toggle btn btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                                        <i className="ti ti-file-export me-1" />
                                        Export
                                    </Link>
                                    <ul className="dropdown-menu dropdown-menu-end p-3">
                                        <li>
                                            <button onClick={() => handleExport("csv")} className="dropdown-item rounded-1">
                                                <i className="ti ti-file-type-pdf me-1" />Export as CSV
                                            </button>
                                        </li>
                                        <li>
                                            <button onClick={() => handleExport("excel")} className="dropdown-item rounded-1">
                                                <i className="ti ti-file-type-xls me-1" />Export as Excel
                                            </button>
                                        </li>
                                        <li>
                                            <button onClick={() => handleExport("pdf")} className="dropdown-item rounded-1">
                                                <i className="ti ti-file-type-xls me-1" />Export as PDF
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        )}
                        <div className="mb-2">
                            {canAdd && (
                                <button onClick={() => handleOpenDrawer(null)} className="btn btn-primary d-flex align-items-center">
                                    <i className="ti ti-circle-plus me-2" />Add Employee
                                </button>
                            )}
                        </div>
                        <div className="head-icons ms-2">
                            <CollapseHeader />
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                        <h5>Employee List</h5>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                            {/* <div className="dropdown me-3">
                                <Link to="#" className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                                    {selectedStatus === "active" ? "Active" : "Inactive"}
                                </Link>
                                <ul className="dropdown-menu dropdown-menu-end p-3">
                                    <li>
                                        <Link to="#" onClick={() => setSelectedStatus("active")} className="dropdown-item rounded-1">
                                            Active
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="#" onClick={() => setSelectedStatus("inactive")} className="dropdown-item rounded-1">
                                            Inactive
                                        </Link>
                                    </li>
                                </ul>
                            </div> */}
                            <div className="dropdown">
                                <Link to="#" className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                                    {selectedSort ? `Sort By : ${selectedSort}` : "Sort By : Recently Added"}
                                </Link>
                                <ul className="dropdown-menu dropdown-menu-end p-3">
                                    <li>
                                        <Link to="#" onClick={() => setSelectedSort("Recently Added")} className="dropdown-item rounded-1">
                                            Recently Added
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="#" onClick={() => setSelectedSort("Ascending")} className="dropdown-item rounded-1">
                                            Ascending
                                        </Link>
                                    </li>
                                    <li>
                                        <Link to="#" onClick={() => setSelectedSort("Descending")} className="dropdown-item rounded-1">
                                            Descending
                                        </Link>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                    <div className="card-body p-0">
                        <Table
                            key={tableKey}
                            dataSource={filteredData}
                            columns={columns}
                            Selection={false}
                        />
                    </div>
                </div>
            </div>

            <EmployeeMappingDrawer
                open={drawerOpen}
                onClose={handleCloseDrawer}
                editRecord={editRecord}
                allEmployees={allEmployees}
                allEmployeesLoading={allEmployeesLoading}
            />

            <DeleteModal1
                isVisible={isDeleteModalVisible}
                onClose={() => setIsDeleteModalVisible(false)}
                onConfirm={handleDelete}
            />
        </div>
    );
};

export default EmployeeMapping;